import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import { IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { Statement } from '@mitel/cloudlink-sdk/admin';
import { ChatConfigDialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/chat-config-dialog/chat-config-dialog.component';
import { AdminService } from 'src/app/core/services/admin.service';
import { IntegPolicyService } from 'src/app/core/services/integ-policy.service';
import { IntegSharedCCService } from 'src/app/core/services/integ-shared-cc.service';
import { Account } from '@mitel/cloudlink-sdk';
import { AccountsService } from './accounts.service';
import { FeatureToggleService } from './feature-toggle.service';
import { SpinnerService } from './spinner.service';

const DisableEditDeleteChatMessageStatement: Statement =  {
	statementId: 'chat-controls',
	action: [
		"PUT",
		"DELETE"
	],
	
	resource: [
		"chat.+api\\.mitel\\.io/[0-9-]+/conversations/[^/]+/messages/[^/]+"
	],
	effect: "Deny" as const
}

const DisableChatAccountAccessStatement: Statement =  {
	statementId: "disable-chat-account-access",
	action: [
		"*"
	],
	resource: [
		"https://mitel.io/auth/mitel-one/features/chat"
	],
	effect: "Deny" as const
}

@Injectable({
	providedIn: 'root'
})

export class IntegChatService implements IntegrationItemServiceInf {

	private adminService: AdminService;
	private policyService: IntegPolicyService;
	private accountsService: AccountsService;
	private featureToggleService: FeatureToggleService;
	private ccService: IntegSharedCCService;
	private spinnerService: SpinnerService;

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.policyService )
			this.policyService = AppInjector.injector.get(IntegPolicyService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.featureToggleService )
			this.featureToggleService = AppInjector.injector.get(FeatureToggleService);

		if ( !this.ccService )
			this.ccService = AppInjector.injector.get(IntegSharedCCService);

		if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);
	}
	
	canBeSelected(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}
	
	// this product can exist as a stand alone product or
	// as a subproduct of another. When the parent product is part of the
	// account, then this product can not be standalone
	// Current parent products are Mitel One and MiVoice5000 and MiVB
	canBeStandalone(account: Account): Promise<boolean>
	{
		this.initServices();

		return this.accountsService.doesAccountHaveAnyOneSpecifiedProduct( account.accountId, ['MitelOne', 'MiVoice5000', 'MiVB'] ).then( productExists => {
			if ( productExists == true )
				return false;
			else
			{
				return true;
			}
		})
		.catch( err => {
			console.log("Fetching tags fails, which can happen but if there are integrations, it should exist", err)
			return true;
		});
	}

	isIntegrationStateChangeable(account: Account): Promise<boolean>
	{
		this.initServices();
		return this.accountsService.doesAccountHaveAnyOneSpecifiedProduct( account.accountId, ['MiVoice5000', 'MiVB'] ).then( productExists => {
			if ( productExists == true )
				return false;
			else
				return  this.ccService.isSetupComplete(account).then(complete => {
					return !complete;
				});

		})
		.catch(err => {
			console.log("Failed to get the account tags", err);
			return true;
		});
	}

	isIntegrationStateOn(account: Account): Promise<boolean>
	{
		this.initServices();

		// check the policy here
		return this.getChatAccountAccessConfiguration(account).then( data => {
			if ( data )
				return Promise.resolve(data.allowChatAccess);
			return Promise.resolve(true);
		})

	}

	getConfigurationDialog() : any
	{
		return ChatConfigDialogComponent;
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		return Promise.resolve(true);
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>
	{
		this.initServices();

		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();
		return this.adminService.getAccountWithoutFrills(account.accountId).then( newAccount => {
			if ( state == true )
			{
				return this.removeChatOverridePolicy(newAccount).then( ret => {
					this.spinnerService.hideSpinner();
					return ret;
				}).catch(err => {
					console.log("Failed to remove policy", err);
					this.spinnerService.hideSpinner();
					throw err;
				});
			}
			else
			{
				// add the policy statement for the account
				return this.policyService.addStatementToPolicy( newAccount, DisableChatAccountAccessStatement).then( policy => {
					if ( newAccount['policyId'] == undefined ) {
						let dataChanges = {
							policyId: policy.policyId,
						}
						return this.adminService.updateAccount( newAccount.accountId, dataChanges).then(result => {
							this.accountsService.mergeAccountWithModel(result);
							this.spinnerService.hideSpinner();
							return policy;
						});
					}
					else
					{
						this.spinnerService.hideSpinner();
						return policy;
					}
				}).catch(err => {
					console.log("Failed to add/update policy", err);
					this.spinnerService.hideSpinner();
					throw err;
				});
			}
		}).catch(err => {
			console.log("Failed to get the account", err);
			this.spinnerService.hideSpinner();
			throw err;
		});

	}

	// When the parent is removed, this is the method called so the child can clean up as well
	// remove the existing chat override policy
	removeAsASubintegration?( account: Account ): Promise<any>
	{
		this.initServices();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();
		return this.adminService.getAccountWithoutFrills(account.accountId).then( newAccount => {
			return this.removeChatOverridePolicy(newAccount).then( ret => {
				this.spinnerService.hideSpinner();
				return ret;
			}).catch(err => {
				// removal is best effort so log it and move on
				console.log("Failed to remove policy", err);//
				this.spinnerService.hideSpinner();
			});
		}).catch(err => {
			console.log("Failed to get the account", err);
			this.spinnerService.hideSpinner();
		});
	}

	getStatusString(account: Account): Promise<StatusString>
	{
		return undefined;
	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		return Promise.resolve(false); // should never be called, but just in case
	}

	handleAccountIntegrationChanges(account: Account, tags: any): Promise<void>
	{
		// the chat integration is only concerned when the Mitel One policy is on and there is a Mitel One integration
		// for the account
		// In this case if the CX integration is setup then make sure the Chat over ride policy is cleared.
		this.initServices();

		if ( tags && tags.products )
		{
			if ( (tags.products.indexOf("MitelOne") != -1) && (tags.products.indexOf("ContactCenter") != -1))
			{
				if ( this.ccService.ccSetupCompleteTagTest(tags) == true )
				{
					// here we must turn off the override chat policy
					return this.removeChatOverridePolicy( account );
				}
				else
					return Promise.resolve();
			}
			else
				return Promise.resolve();
		}
		else
			return Promise.resolve();

	}

	removeChatOverridePolicy( account: Account ): Promise<any>
	{
		// remove the account policy statement
		if ( account['policyId'] === undefined ) // nothing to remove
		{
			return Promise.resolve({});
		}
		return this.adminService.getPolicy(account.accountId, account['policyId']).then( policy => {
			console.log("Here are the account policies", policy);
			const foundPolicy = policy.statements.find(stmt => {
				return stmt.statementId === DisableChatAccountAccessStatement.statementId;
			});
			if ( foundPolicy )
			{
				return this.policyService.removeStatementFromPolicy(account, policy, DisableChatAccountAccessStatement);
			}
			else
				return Promise.resolve({});
		});
	}

	getChatConfiguration(account: Account): Promise<any>
	{
		this.initServices();

		let config = {
			allowEditDelete: true
		};

		return this.adminService.getAccountWithoutFrills(account.accountId).then( newAccount => {
			if ( newAccount['policyId'] != undefined )
			{
				return this.adminService.getPolicy(account.accountId, newAccount['policyId']).then( policy => {
					console.log("Here are the account policies", policy);
					const foundPolicy = policy.statements.find(stmt => {
						return stmt.statementId === DisableEditDeleteChatMessageStatement.statementId;
					});
					if ( foundPolicy )
					{
						config.allowEditDelete = false; // the policy being present means it is not allows
					}
					return config;
				}) 
				.catch(err => {
					console.log("Here is the error from getting policies", err)
					throw err;
				});
			}
			else
				return config;
		}).catch(err => {
			console.log("Failed to get account", account,  err);
			throw err;
		});
	}

	saveChatConfig( account: Account, config: any ): Promise<any>
	{
		this.initServices();
		// it has been proven that other feature are changing the policy and the account passed in might not have the 
		// accuarate data at the moment

		return this.adminService.getAccountWithoutFrills(account.accountId).then( newAccount => {
			if ( config.allowEditDelete == true )
			{
				// remove the account policy statement
				if ( newAccount['policyId'] === undefined ) // nothing to remove
				{
					return Promise.resolve({});
				}
				return this.adminService.getPolicy(account.accountId, newAccount['policyId']).then( policy => {
					console.log("Here are the account policies", policy);
					const foundPolicy = policy.statements.find(stmt => {
						return stmt.statementId === DisableEditDeleteChatMessageStatement.statementId;
					});
					if ( foundPolicy )
					{
						return this.policyService.removeStatementFromPolicy(newAccount, policy, DisableEditDeleteChatMessageStatement);
					}
					return Promise.resolve({});
				});

			}
			else
			{
				// add the policy statement for the account
				return this.policyService.addStatementToPolicy( newAccount, DisableEditDeleteChatMessageStatement).then( policy => {
					if ( newAccount['policyId'] == undefined ) {
						let dataChanges = {
							policyId: policy.policyId,
						}
						return this.adminService.updateAccount( newAccount.accountId, dataChanges).then(result => {
							this.accountsService.mergeAccountWithModel(result);
							return policy;
						});
					}
					else
						return policy;
				}).catch(err => {
					console.log("Failed to add/update policy", err);
					throw err;
				});
			}
		}).catch(err => {
			console.log("Failed to get the account", err);
			throw err;
		});
	}

	getChatAccountAccessConfiguration(account: Account): Promise<any>
	{
		this.initServices();

		let config = {
			allowChatAccess: true
		};

		return this.adminService.getAccountWithoutFrills(account.accountId).then( newAccount => {
			if ( newAccount['policyId'] != undefined )
			{
				return this.adminService.getPolicy(account.accountId, newAccount['policyId']).then( policy => {
					console.log("Here are the account policies", policy);
					const foundPolicy = policy.statements.find(stmt => {
						return stmt.statementId === DisableChatAccountAccessStatement.statementId;
					});
					if ( foundPolicy )
					{
						config.allowChatAccess = false; // the policy being present means it is not allows
					}
					return config;
				}) 
				.catch(err => {
					console.log("Here is the error from getting policies", err)
					throw err;
				});
			}
			else
				return config;
		}).catch(err => {
			console.log("Failed to get account", account,  err);
			throw err;
		});
	}

	addIntegrationByDefault(tags?: any): Promise<boolean> {
		return Promise.resolve(true);
	}
}
